import { isOperative } from "@/lib/api";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Login() {
  const { login, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(() => {
    const reason = window.sessionStorage.getItem("talleros_logout_reason");
    if (reason) window.sessionStorage.removeItem("talleros_logout_reason");
    return reason;
  });

  useEffect(() => {
    if (session) {
      const from = location.state?.from?.pathname;
      const to = isOperative(session.user) ? "/mis-trabajos" : from || "/dashboard";
      navigate(to, { replace: true });
    }
  }, [session, navigate, location.state]);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    const res = await login(email.trim(), password);
    if (!res.ok) setErr(res.error);
    setSubmitting(false);
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left: hero */}
      <div className="hero-grid relative hidden lg:block">
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img
              src="/logo-principal.png"
              alt="TallerOS"
              data-testid="login-brand-logo"
              className="h-12 w-12 rounded-md object-contain ring-1 ring-white/20 shadow-lg"
            />
            <div>
              <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                TallerOS
              </div>
              <div className="text-[11px] uppercase tracking-widest text-white/70">
                by VRIntegrales
              </div>
            </div>
          </div>

          <div className="max-w-md">
            <h2 className="text-4xl xl:text-5xl leading-tight tracking-tight mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
              El sistema operativo de tu taller mecánico.
            </h2>
            <p className="text-white/80 text-base leading-relaxed">
              Multi-sucursal, multi-usuario y con branding propio. Diseñado para que
              administres clientes, vehículos y operación desde un solo lugar.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-sm">
              {[
                { k: "Multi", v: "tenant" },
                { k: "Multi", v: "sucursal" },
                { k: "Roles", v: "granular" },
              ].map((f) => (
                <div key={f.v} className="border-l-2 border-white/40 pl-3">
                  <div className="text-[11px] text-white/60 uppercase tracking-wider">{f.k}</div>
                  <div className="text-sm font-medium">{f.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-white/60 tracking-wider uppercase">
            © VRIntegrales · Etapa 1: Base multi-tenant
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-16 relative bg-white">
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <img
            src="/logo-principal.png"
            alt="TallerOS"
            data-testid="login-brand-logo-mobile"
            className="h-11 w-11 rounded-md object-contain ring-1 ring-slate-200"
          />
          <div>
            <div className="text-lg font-semibold tracking-tight">TallerOS</div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              by VRIntegrales
            </div>
          </div>
        </div>

        <div className="max-w-md w-full">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">
            Iniciar sesión
          </div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">
            Bienvenido de vuelta
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            Ingresa con tu correo y contraseña de TallerOS.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            {err && (
              <Alert variant="destructive" data-testid="login-error">
                <AlertDescription>{err}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                data-testid="login-email"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 btn-tenant"
              data-testid="login-submit"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Entrar
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
